'use client'

import { useEffect, useRef, useState } from 'react'
import { Fraunces } from 'next/font/google'
import { translate, siteLanguages, sitePaths, type SiteCopy, type SiteLocale } from '../content'


import styles from '../peacecover.module.css'
import HeroScrollButton from './HeroScrollButton'
import RotatingWord from './RotatingWord'
import StickyWordVideoSection from './StickyWordVideoSection'

const fraunces = Fraunces({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
  style: ['normal', 'italic'],
})

export default function PeacecoverPageClient({ locale, copy }: { locale: SiteLocale; copy: SiteCopy }) {
  const t = translate(copy)
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false)
  const langMenuRef = useRef<HTMLDivElement>(null)
  const activeLangLabel = { ko: 'KOR', en: 'ENG', ja: 'JP' }[locale]

  useEffect(() => {
    document.documentElement.lang = locale
    // Preserve old shared links while keeping each canonical URL language-specific.
    const oldLocale = new URLSearchParams(window.location.search).get('locale')
    const target = oldLocale === 'jp' || oldLocale === 'ja' ? 'ja' : oldLocale === 'ko' ? 'ko' : oldLocale === 'en' ? 'en' : null
    if (target && target !== locale) window.location.replace(sitePaths[target] + window.location.hash)
  }, [locale])

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!langMenuRef.current || langMenuRef.current.contains(event.target as Node)) {
        return
      }
      setIsLangMenuOpen(false)
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsLangMenuOpen(false)
      }
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  return (
    <main lang={locale} className={`${styles.page} ${styles.uiFont}`}>
      <div className={styles.pageBackdrop} aria-hidden="true" />

      <header className={styles.navbar}>
        <span className={styles.logo} role="img" aria-label="FanHeart" />
        <nav className={styles.navLinks} aria-label="Primary">
          <a href="#review">{t('nav.review')}</a>
          <a href="#contact">{t('nav.contact')}</a>
          <div className={styles.langMenuWrap} ref={langMenuRef}>
            <button
              type="button"
              className={styles.langBtn}
              aria-haspopup="menu"
              aria-expanded={isLangMenuOpen}
              onClick={() => setIsLangMenuOpen((prev) => !prev)}
            >
              {activeLangLabel}
            </button>
            {isLangMenuOpen && (
              <div className={styles.langMenuList} role="menu" aria-label="Language selector">
                {siteLanguages.map((item) => (
                  <a
                    key={item.code}
                    href={sitePaths[item.code]}
                    hrefLang={item.code}
                    lang={item.code}
                    className={styles.langMenuItem}
                    role="menuitemradio"
                    aria-checked={locale === item.code}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </nav>
      </header>

      <div className={styles.heroStage}>
        <section className={styles.hero}>
          <div className={styles.artWrap} aria-hidden="true">
            <div className={`${styles.heartGem} ${styles.heartGemBlur}`}>
              <div className={styles.heartBlurCore}>
                <div className={styles.heartPanels}>
                  <div className={`${styles.heartPanel} ${styles.heartPanelLeft}`} />
                  <div className={`${styles.heartPanel} ${styles.heartPanelRight}`} />
                </div>
              </div>
            </div>
            <div className={`${styles.heartGem} ${styles.heartGemTop}`}>
              <div className={styles.heartPanels}>
                <div className={`${styles.heartPanel} ${styles.heartPanelLeft}`} />
                <div className={`${styles.heartPanel} ${styles.heartPanelRight}`} />
              </div>
            </div>
            <div className={styles.tailLightNoise} />
          </div>

          <div className={styles.copy}>
            <h1 lang="en" className={`${styles.headingFont} ${fraunces.className}`}>
              {t('hero.title.line1')}
              <br className={styles.heroLineBreak} />
              <span className={styles.secondLine}>
                <span>{t('hero.title.line2Prefix')}</span>
                <span className={styles.wordRotator} aria-label={t('hero.rotating.artist')}>
                  <RotatingWord className={styles.italicFont} words={[
                    t('hero.rotating.artist'),
                    t('hero.rotating.series'),
                    t('hero.rotating.movie'),
                    t('hero.rotating.character'),
                    t('hero.rotating.novel'),
                  ]}
                  />
                </span>
                <span>{t('hero.title.line2Suffix')}</span>
              </span>
            </h1>
            <p>{t('hero.description')}</p>
          </div>
        </section>
        <div className={styles.tailBottomGradient} aria-hidden="true" />
        <HeroScrollButton
          className={styles.scrollCta}
          label={t('hero.scroll')}
          ariaLabel={t('hero.scrollAria')}
        />
      </div>

      <StickyWordVideoSection copy={copy} />
    </main>
  )
}
